import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
    EstensaBusWrapperService,
    ExaminationStatusForImaging,
    ImagingService,
    KeyImage,
    KeyImagesService,
    RootState,
    seExaminationAssociation,
    selectSeReportKeyImages,
    seReportDeleteKeyImages,
    seReportSetImagesPerRowInPlaceholder,
    seReportSetKeyImages,
    seReportSetPatientId,
    seViewerKeyImageAdd,
    seViewerKeyImageRefresh,
    seViewerKeyImagesDelete,
    seViewerKeyImagesPerRows,
    seViewerKeyImagesSet,
} from '@se/platform';
import {
    ButtonClick,
    IModalDialogNotification,
    Logger,
    ModalDialogNotificationService,
    NotificationType,
} from '@se/se.ui.html5';
import { intersectionBy, isEqual } from 'lodash-es';
import {
    catchError,
    distinctUntilChanged,
    exhaustMap,
    filter,
    from,
    map,
    mergeMap,
    of,
    pairwise,
    startWith,
    Subscription,
    switchMap,
    take,
    tap,
    withLatestFrom,
} from 'rxjs';

import { isNotNullOrUndefined } from '../cp-configuration/utils/utils';
import { isEntity } from '../cp-configuration/view-model';
import { CpReportService, ReportStatus } from '../cp-report.service';
import {
    cpEmpty,
    cpInitialize,
    cpOpenImagesForCurrentExamination,
    cpSafeExit,
    cpSave,
    cpShowErrorModal,
} from './cp.actions';
import { CpEffects } from './cp.effects';
import { selectCpModules } from './cp.selectors';

export function reportStatusToImagingStatus(
    oldStatus: ReportStatus | undefined,
    newStatus: ReportStatus | undefined,
): ExaminationStatusForImaging {
    if (!newStatus) {
        return 'Unknown';
    }
    // Stato iniziale
    if (!oldStatus) {
        if (newStatus.isApproved || newStatus.isDigitallySigned) {
            return 'Closed';
        }
        return 'Open';
    }
    // Firma o definitivo
    if (
        (!oldStatus.isApproved || !oldStatus.isDigitallySigned) &&
        (newStatus.isApproved || newStatus.isDigitallySigned)
    ) {
        return 'Close';
    }
    // Annulla firma o definitivo
    if (
        (oldStatus.isApproved || oldStatus.isDigitallySigned) &&
        (!newStatus.isApproved || !newStatus.isDigitallySigned)
    ) {
        return 'ReOpened';
    }

    return 'Open';
}

@Injectable()
export class CpImagingEffects {
    initializePage$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(cpInitialize),
                tap(() => {
                    this.registerModelListeners();
                }),
            ),
        { dispatch: false },
    );

    /**
     * Effettua la pulizia dei vari servizi.
     */
    safeExit$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(cpSafeExit),
                tap(() => this.safeExit()),
            ),
        { dispatch: false },
    );

    examinationAssociation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(seExaminationAssociation),
            // Mostra la modale solo se si sta refertando l'examination notificata.
            filter(({ idExamination }) =>
                isEntity(this.reportService.examinations?.find((ex) => ex.Id === idExamination)),
            ),
            switchMap(() => this.reportService.canSave$.pipe(take(1))),
            exhaustMap((canSave) => {
                if (canSave) {
                    return this.askForReload(true);
                }

                return this.notifyAssociation();
            }),
        ),
    );

    keyImageAdd$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(seViewerKeyImageAdd),
                tap((action) => {
                    try {
                        this.reportService.addKeyImage(action.image);
                    } catch (err) {
                        if (err instanceof Error) {
                            this.logger.error(`Failed to add key image: ${err.message}`);
                        }
                    }
                }),
            ),
        { dispatch: false },
    );

    keyImageDeleteFromReport$ = createEffect(() =>
        this.actions$.pipe(
            ofType(seReportDeleteKeyImages),
            mergeMap(({ keyImages }) =>
                this.keyImagesService.deleteKeyImages(keyImages.map((img) => img.RepositoryKey)).pipe(
                    tap(() => this.reportService.deleteKeyImages(keyImages)),
                    exhaustMap(() => this.cpEffects.save()),
                    //tap(() => { throw new Error("This is an exception.");  } ), // TODO DGS: Verificare con dati puliti
                    exhaustMap(() => from(this.imagingService.deleteKeyImages(keyImages))),
                    map(() => seReportSetKeyImages({ keyImages: this.reportService.keyImages })),
                    catchError((err) => {
                        if (err instanceof Error) {
                            this.logger.error(`Failed to add key image: ${err.message}`);
                        }
                        return of(
                            cpShowErrorModal({
                                title: 'KeyImages',
                                message: 'KeyImageDeleteError',
                            }),
                            ...this.addKeyImages(keyImages),
                        );
                    }),
                ),
            ),
        ),
    );

    keyImageDeleteFromViewer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(seViewerKeyImagesDelete),
            map(({ images }) => {
                try {
                    this.reportService.deleteKeyImages(images);
                    return seReportSetKeyImages({ keyImages: this.reportService.keyImages });
                } catch (err) {
                    if (err instanceof Error) {
                        this.logger.error(`Failed to add key image: ${err.message}`);
                    }
                    return cpShowErrorModal({ title: 'KeyImages', message: 'KeyImageDeleteError' });
                }
            }),
        ),
    );

    imagesPerRowInPlaceholder$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(seViewerKeyImagesPerRows),
                tap((action) => {
                    try {
                        const imagesPerRow = action.rows ?? 1;
                        this.reportService.setImagesPerRowInPlaceholder(imagesPerRow);
                        seReportSetImagesPerRowInPlaceholder({ imagesPerRowInPlaceholder: imagesPerRow });
                    } catch (err) {
                        if (err instanceof Error) {
                            this.logger.error(`Failed to add images per row in placeholder: ${err.message}`);
                        }
                    }
                }),
            ),
        { dispatch: false },
    );

    keyImageRefresh$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(seViewerKeyImageRefresh),
                tap((action) => {
                    try {
                        this.reportService.updateKeyImage(action.image);
                    } catch (err) {
                        if (err instanceof Error) {
                            this.logger.error(`Failed to refresh key image: ${err.message}`);
                        }
                    }
                }),
            ),
        { dispatch: false },
    );

    keyImagesSet$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(seViewerKeyImagesSet),
                tap((action) => {
                    try {
                        this.reportService.setKeyImages(action.images);
                    } catch (err) {
                        if (err instanceof Error) {
                            this.logger.error(`Failed to set key images: ${err.message}`);
                        }
                    }
                }),
            ),
        { dispatch: false },
    );

    openImages$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(cpOpenImagesForCurrentExamination),
                exhaustMap(() =>
                    from(this.openImagesForCurrentExamination()).pipe(
                        catchError((err) => {
                            if (err instanceof Error) {
                                this.logger.error(`Failed to open images: ${err.message}`);
                            }
                            return of(cpEmpty());
                        }),
                    ),
                ),
            ),
        { dispatch: false },
    );

    private modelSubscriptions: Subscription[] = [];

    private readonly examinationStatus$ = () =>
        this.reportService.reportStatus$.pipe(
            startWith(undefined),
            pairwise(),
            map(([a, b]) => reportStatusToImagingStatus(a, b)),
            withLatestFrom(this.reportService.examinationIds$),
            filter(([_, examinationIds]) => examinationIds.length > 0),
            distinctUntilChanged(isEqual),
        );

    private readonly usExamination$ = () =>
        this.store.pipe(
            // Nel caso l'esame aperto è un US...
            select(selectCpModules),
            filter((mods) => mods.includes('Us')),
            // Quando cambia la ReferenceExamination...
            switchMap(() => this.reportService.referenceExaminationId$),
            // E se non è nulla (la pagina è stata chiusa).
            filter(isNotNullOrUndefined),
            distinctUntilChanged(),
        );

    private readonly keyImages$ = () =>
        this.reportService.keyImages$.pipe(
            withLatestFrom(this.store.pipe(select(selectSeReportKeyImages))),
            filter(([imagesFromReport, imagesFromStore]) => {
                // Notifichiamo una modifica solo se ci sono effettivamente delle immagini modificate.
                // Quindi se il numero di immagini è diverso o se ci sono immagini nel report che non sono
                //   nello store.
                if (imagesFromReport.length !== imagesFromStore.length) {
                    return true;
                }
                const intersection = intersectionBy(imagesFromReport, imagesFromStore, 'SOPInstanceUID');
                return intersection.length !== imagesFromReport.length;
            }),
        );

    constructor(
        private actions$: Actions,
        private cpEffects: CpEffects,
        private estensaBus: EstensaBusWrapperService,
        private imagingService: ImagingService,
        private keyImagesService: KeyImagesService,
        private logger: Logger,
        private modalDialogNotificationService: ModalDialogNotificationService,
        private reportService: CpReportService,
        private store: Store<RootState>,
        private translate: TranslateService,
    ) {
    }

    safeExit() {
        try {
            this.cleanupModelListeners();
            this.imagingService.discardImages();
            this.store.dispatch(seReportSetPatientId({ patientId: null }));
            this.store.dispatch(seReportSetKeyImages({ keyImages: [] }));
        } catch (err) {
            // Non far nulla per ora.
        }
    }

    // #region Helpers

    private askForReload(forceOk = false) {
        const notification: IModalDialogNotification = {
            buttonCancel: false,
            buttonNo: !forceOk,
            buttonOk: forceOk,
            buttonYes: !forceOk,
            message: this.translate.instant('NewAssociationReload'),
            title: 'Warning',
            type: NotificationType.warning,
        };

        // Mappa il pulsante della modale ad un'azione da scatenare.
        return this.modalDialogNotificationService.notify(notification).pipe(
            take(1),
            map((btn) => (btn === ButtonClick.yes || forceOk ? cpSave({ forcedReload: true }) : cpEmpty())),
        );
    }

    private notifyAssociation() {
        const notification: IModalDialogNotification = {
            buttonCancel: false,
            buttonNo: false,
            buttonOk: true,
            buttonYes: false,
            message: this.translate.instant('NewAssociationAvailable'),
            title: 'Warning',
            type: NotificationType.warning,
        };

        return this.modalDialogNotificationService.notify(notification).pipe(
            take(1),
            map(() => cpEmpty()),
        );
    }

    private cleanupModelListeners() {
        // Sincronizza lo stato del modello e quello dello store del report.
        for (const sub of this.modelSubscriptions) {
            sub.unsubscribe();
        }
        this.modelSubscriptions = [];
    }

    private registerModelListeners() {
        this.modelSubscriptions.push(
            this.keyImages$().subscribe(([keyImages]) => this.store.dispatch(seReportSetKeyImages({ keyImages }))),
        );

        this.store.pipe(select(selectSeReportKeyImages)).subscribe((images) => this.reportService.setKeyImages(images));

        // È necessario comunicare al viewer il cambiamento dello stato del referto.
        this.modelSubscriptions.push(
            this.examinationStatus$().subscribe(([status, examinationIds]) => {
                this.imagingService.setExaminationStatus(examinationIds, status);
            }),
        );

        // Apri le immagini quando si apre un'examination.
        this.modelSubscriptions.push(
            this.usExamination$().subscribe(() => this.store.dispatch(cpOpenImagesForCurrentExamination())),
        );
    }

    private async openImagesForCurrentExamination() {
        const examinationEntity = this.reportService.referenceExamination;
        if (!examinationEntity) {
            return;
        }

        this.estensaBus.notify({
            messageType: 'BROKER_CHECK_REQUEST',
            data: {
                examinationIds: this.reportService.examinationIds ?? [],
            },
        });
    }

    private addKeyImages(keyImages: KeyImage[]): Array<any> {
        return keyImages.map((keyImage: KeyImage) => seViewerKeyImageAdd({ image: keyImage }));
    }

    // #endregion
}


